<template>
  <section>
    <v-btn
      class="mt-1 mr-n3"
      fab
      dark
      x-small
      color="red"
      :absolute="true"
      :right="true"
      title="Remove this prompt."
      @click="remove(scene)"
    >
      <v-icon dark>mdi-delete</v-icon>
    </v-btn>
    <v-row align="start">
      <v-col cols="9">
        <v-textarea
          label="Prompt message*"
          hint="This is the message the user will receive to prompt their input"
          v-model="scene.prompt.message"
          :rows="2"
          :rules="[(v) => !!v || 'Prompt message is required']"
          auto-grow
          validate-on-blur
          required
        ></v-textarea>
      </v-col>
      <v-col
        cols="3"
        align-self="center"
        class="d-flex justify-center"
        v-if="scene.type && scene.type === 'text'"
      >
        <v-switch
          v-model="scene.options.required"
          label="Required"
          hint="Can now be skipped"
          :persistent-hint="!scene.options.required"
        ></v-switch>
      </v-col>
    </v-row>
    <v-row align="start">
      <v-col cols="12" md="4">
        <v-text-field
          label="ID*"
          hint="A short tag to identify this prompt (only use a-z and _)"
          v-model="scene.id"
          :error-messages="
            error.errors &&
            error.errors.scene &&
            error.errors.scene.index === index
              ? error.errors.scene.id
              : null
          "
          :rules="idRules"
          validate-on-blur
          required
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          label="Type*"
          v-model="scene.type"
          :items="types"
          :rules="[(v) => !!v || 'Type is required']"
          hint="Specify the type of prompt"
          validate-on-blur
          required
        ></v-select>
      </v-col>
      <v-col cols="12" md="4" v-if="scene.type === 'text'">
        <v-select
          label="Expect"
          v-model="scene.validation.expect"
          :items="expect"
          hint="What is the expected input from the user"
        ></v-select>
      </v-col>
      <v-col cols="12" md="4" v-if="scene.type === 'photo'">
        <v-slider
          v-model="scene.options.photoLimit"
          class="align-end"
          label="Photos"
          max="3"
          min="1"
          hide-details
        >
          <template v-slot:append>
            <v-text-field
              v-model="scene.options.photoLimit"
              class
              hide-details
              single-line
              type="number"
              style="width: 40px"
              disabled
            ></v-text-field>
          </template>
        </v-slider>
      </v-col>
    </v-row>
    <v-row align="start" v-if="scene.type === 'select'">
      <v-col :cols="scene.options.icons && Object.keys(scene.options.icons).length ? '9' : '12'">
        <v-combobox
          v-model="scene.prompt.options"
          hide-selected
          ref="selectCB"
          hint="Maximum of 10 options"
          label="Select options"
          :rules="selectRules"
          multiple
          persistent-hint
          small-chips
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  Type option text and press
                  <kbd>enter</kbd> to create a new one
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
          <template #selection="{ item, index }">
            <div style="position: relative;">
              <emoji
                position="right"
                :show.sync="setIcon[index]"
                :emoji-url.sync="scene.options.icons[item]"
                @emoji-set="updateScene()"
              ></emoji>
              <v-chip 
                close
                @click.stop="setSelectIcon(index)"
                @click:close="removeSelectItem(item)"
              >
                <v-avatar
                  class="accent white--text"
                  left
                >
                  <v-img :src="scene.options.icons[item]"></v-img>
                </v-avatar>
                {{item}}
              </v-chip>
            </div>
          </template>
        </v-combobox>
      </v-col>
      <v-col v-if="scene.prompt.options && scene.prompt.options.length > 1" cols="6" class="pt-0">
        <v-checkbox
          v-model="scene.prompt.multiple"
          label="Allow multiple selection"
        ></v-checkbox>
      </v-col>
      <v-col v-if="(scene.options.icons && Object.keys(scene.options.icons).length) 
        && ((scene.prompt.multiple && scene.prompt.options && scene.prompt.options.length === 1) || !scene.prompt.multiple)" 
        cols="6" class="pt-0"
      >
        <v-checkbox
          v-model="scene.options.useIconsOnMap"
          label="Use these icons on map"
        ></v-checkbox>
      </v-col>
    </v-row>
    <v-row
      align="start"
      v-if="filteredConditionScenes && filteredConditionScenes.length"
    >
      <v-col cols="12">
        <v-subheader class="title">Conditions</v-subheader>
        <v-divider></v-divider>
        <p class="mt-2">
          Only prompt for this input if the following condition/s are met.
        </p>
      </v-col>
    </v-row>
    <v-row
      align="start"
      v-if="filteredConditionScenes && filteredConditionScenes.length"
    >
      <v-col cols="12" md="4">
        <v-select
          label="Prompt Id"
          v-model="conditionScene"
          :items="filteredConditionScenes"
          :return-object="true"
          item-text="id"
          hint="The prompt ID used for this condition"
        ></v-select>
      </v-col>
      <v-col
        class="text-center"
        cols="12"
        md="4"
        v-if="
          conditionScene &&
          conditionScene.prompt &&
          conditionScene.prompt.options
        "
        align-self="center"
      >
        <v-select
          label="Prompt value"
          v-model="conditionValue"
          :items="conditionScene.prompt.options"
          :multiple="true"
          hint="The value for the prompt ID that will conditionally allow this prompt"
        ></v-select>
      </v-col>
      <v-col
        class="text-center"
        cols="12"
        md="4"
        v-if="conditionScene && conditionScene.type === 'binary'"
        align-self="center"
      >
        <v-radio-group v-model="conditionValue" row>
          <v-radio label="Yes" :value="true"></v-radio>
          <v-radio label="No" :value="false"></v-radio>
        </v-radio-group>
      </v-col>
      <v-col
        class="text-center"
        cols="12"
        md="4"
        v-if="
          conditionScene &&
          conditionScene.type === 'text' &&
          conditionScene.validation &&
          conditionScene.validation.expect === 'number'
        "
        align-self="center"
      >
        <v-select
          v-model="conditionValue.op"
          :items="operators"
          label="Operator"
        ></v-select>
      </v-col>
      <v-col
        class="text-center"
        cols="12"
        md="4"
        v-if="
          conditionScene &&
          conditionScene.type === 'text' &&
          conditionScene.validation &&
          conditionScene.validation.expect === 'number'
        "
        align-self="center"
      >
        <v-text-field
          v-model="conditionValue.value"
          label="Value"
          single-line
        ></v-text-field>
      </v-col>
      <v-col
        cols="12"
        align="end"
        v-if="
          conditionScene.id &&
          conditionValue !== null &&
          conditionValue !== undefined
        "
      >
        <v-btn small color="primary" dark @click.stop="addCondition()"
          >Add Condition</v-btn
        >
      </v-col>
    </v-row>
    <v-row align="start" v-if="scene.conditions && scene.conditions.length">
      <v-col cols="12">
        <v-subheader class="title">Conditions</v-subheader>
        <v-divider></v-divider>
        <p class="mt-2">
          Will only prompt for this input if the following condition/s are met.
        </p>
      </v-col>
      <v-col cols="12" md="6" align="start">
        <v-list class="transparent">
          <v-list-item
            v-for="condition in scene.conditions"
            :key="condition.id"
          >
            <v-list-item-action>
              <v-icon color="red" @click="removeCondition(condition)"
                >mdi-delete</v-icon
              >
            </v-list-item-action>
            <v-list-item-title>
              <v-chip class="subtitle-1 font-weight-bold">{{
                condition.id
              }}</v-chip>
            </v-list-item-title>
            <v-list-item-icon>
              <v-icon v-if="!condition.value.op">mdi-equal</v-icon>
              <v-icon v-if="condition.value.op">{{
                opIcon[condition.value.op]
              }}</v-icon>
            </v-list-item-icon>
            <v-list-item-subtitle class="ml-4 subtitle-1">{{
              Array.isArray(condition.value)
                ? condition.value.join(" / ")
                : condition.value.op
                ? condition.value.value
                : condition.value
                ? "Yes"
                : "No"
            }}</v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </v-col>
      <v-col cols="12" md="6" align="start">
        Which conditions must be met?
        <v-select v-model="scene.options.conditionsOperator" :items="conditionsOperators" @change="updateScene()"></v-select>
      </v-col>
    </v-row>
  </section>
</template>

<script>
import Emoji from "../widget/Emoji";

export default {
  data: () => ({
    conditionScene: {},
    conditionIds: [],
    conditionValue: null,
    selectedCondition: null,
    setIcon: {},
    types: [
      { text: "Text", value: "text" },
      { text: "Select", value: "select" },
      { text: "Yes/No", value: "binary" },
      { text: "License Plate", value: "license_plate" },
      { text: "Reference", value: "reference" },
      { text: "Location", value: "location" },
      { text: "Coordinates", value: "coordinates" },
      { text: "Address", value: "address" },
      { text: "Photo", value: "photo" },
      { text: "File", value: "file" },
    ],
    expect: [
      { text: "Name", value: "name" },
      { text: "Date", value: "date" },
      { text: "Time", value: "time" },
      { text: "Email", value: "email" },
      { text: "Number", value: "number" },
      { text: "Reference", value: "reference" },
    ],
    operators: [
      { text: "Equal", value: "eq" },
      { text: "Not equal", value: "neq" },
      { text: "Less than", value: "lt" },
      { text: "Greater than", value: "gt" },
    ],
    opIcon: {
      eq: "mdi-equal",
      neq: "mdi-not-equal-variant",
      lt: "mdi-less-than",
      gt: "mdi-greater-than",
    },
    conditionsOperators: [
      { text: "All", value: "and" },
      { text: "Any", value: "or" },
    ],
    selectRules: [
      (v) => !!v.length || "Please add at least one selection option",
      (v) => (v || "").length <= 15 || "Maximum of 15 options allowed",
    ],
  }),
  /*created() {
    if (this.scene.options === undefined) this.scene.options = {};
    if (this.scene.options.icons === undefined) this.scene.options.icons = {};
  },*/
  mounted() {
    if (this.scene.options === undefined) this.scene.options = {};
    if (this.scene.options.conditionsOperator === undefined) this.scene.options.conditionsOperator = 'and';
    if (this.scene.options.icons === undefined) this.scene.options.icons = {};
    this.setSceneConditionIds();
  },
  computed: {
    /*scene() {
      return this.value;
    },*/
    idRules() {
      return [
        (v) => !!v || "Id is required",
        () => !this.idExists || "Id in use, please use another Id",
        (v) => /^[a-z_]*$/g.test(v) || 'Invalid Id, only "a-z" and "_" allowed',
      ];
    },
    idExists() {
      return (
        this.scenes.find((scene) => {
          return scene.id === this.scene.id && scene.index !== this.scene.index;
        }) !== undefined
      );
    },
    filteredConditionScenes() {
      return this.conditionScenes.filter((scene) => {
        return (
          (!this.scene.id || scene.id !== this.scene.id) &&
          scene.index <= this.scene.index &&
          !this.conditionIds.includes(scene.id)
        );
      });
    },
  },
  methods: {
    addCondition() {
      let condition = {
        id: this.conditionScene.id,
        value: this.conditionValue,
      };
      this.scene.conditions.push(condition);
      this.conditionScene = {};
      this.conditionValue = null;
      this.setSceneConditionIds();
    },
    removeCondition(condition) {
      let index;
      this.scene.conditions.forEach((sceneCondition, idx) => {
        if (sceneCondition.id === condition.id) index = idx;
      });

      if (index !== undefined) this.scene.conditions.splice(index, 1);
      this.setSceneConditionIds();
    },
    removeSelectItem(item) {
      const index = this.scene.prompt.options.indexOf(item)
      if (index >= 0) this.scene.prompt.options.splice(index, 1)
    },
    setSceneConditionIds() {
      this.conditionIds = [];

      this.scene.conditions.forEach((condition) => {
        this.conditionIds.push(condition.id);
      });
    },
    setSelectIcon(index) {
      this.setIcon[index] = (this.setIcon[index] !== undefined) ? !this.setIcon[index] : true;
      this.$refs.selectCB.blur();
    },
    updateScene() {
      this.$emit("changed", true);
    },
  },
  watch: {
    "conditionScene.type": {
      handler(type) {
        if (type === "text") this.conditionValue = {};
        if (type !== "text") this.conditionValue = null;
      },
    },
    "scene.conditions": {
      handler(now, before) {
        if (now !== before) {
          this.setSceneConditionIds();
        }
      },
    },
    scene: {
      handler() {
        if (this.scene.options === undefined) this.scene.options = {};
        if (this.scene.options.icons === undefined) this.scene.options.icons = {};
      },
      deep: true
    }
  },
  components: {
    Emoji,
  },
  props: {
    /*value: {
      type: Object,
      default: () => {
        return {};
      }
    },*/
    scene: {
      type: Object,
      default: () => {
        return {};
      },
    },
    scenes: {
      type: Array,
      default: () => {
        return [];
      },
    },
    conditionScenes: {
      type: Array,
      default: () => {
        return [];
      },
    },
    error: {
      type: Object,
      default: () => {
        return {};
      },
    },
    remove: {
      type: Function,
    },
  },
};
</script>

<style lang="scss" scoped>
</style>